<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCallsStore } from 'dashboard/stores/calls';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import {
  DIALPAD_KEYS,
  normalizePhoneNumber,
  isValidDialpadNumber,
  appendDigit,
  backspaceDigit,
  findContactByPhone,
  createContact,
} from 'dashboard/components-next/Dialpad/helpers/dialpadHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  voiceInboxes: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const callsStore = useCallsStore();

const dialogRef = ref(null);

const rawNumber = ref('');
const customerName = ref('');
const showNameField = ref(false);
const matchedContact = ref(null);
const selectedInboxId = ref(null);
const isProcessing = ref(false);

const normalizedNumber = computed(() => normalizePhoneNumber(rawNumber.value));
const isNumberValid = computed(() => isValidDialpadNumber(rawNumber.value));
const hasMultipleInboxes = computed(() => props.voiceInboxes.length > 1);
const inboxOptions = computed(() =>
  props.voiceInboxes.map(inbox => ({
    value: inbox.id,
    label: inbox.phone_number
      ? `${inbox.name} (${inbox.phone_number})`
      : inbox.name,
  }))
);

const callingName = computed(
  () =>
    matchedContact.value?.name ||
    customerName.value.trim() ||
    normalizedNumber.value
);
const isCallingStatusVisible = computed(
  () => isProcessing.value && !!matchedContact.value
);

const callButtonDisabled = computed(() => {
  if (isProcessing.value) return true;
  if (!rawNumber.value) return true;
  if (showNameField.value && !customerName.value.trim()) return true;
  return false;
});

const callButtonLabel = computed(() =>
  isProcessing.value
    ? t('DIALPAD.CALLING', { name: callingName.value })
    : t('DIALPAD.CALL')
);

const resetState = () => {
  rawNumber.value = '';
  customerName.value = '';
  showNameField.value = false;
  matchedContact.value = null;
  isProcessing.value = false;
  selectedInboxId.value = props.voiceInboxes[0]?.id ?? null;
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

defineExpose({ open, close });

const onDigitPress = digit => {
  rawNumber.value = appendDigit(rawNumber.value, digit);
  showNameField.value = false;
  matchedContact.value = null;
};

const onBackspace = () => {
  rawNumber.value = backspaceDigit(rawNumber.value);
  showNameField.value = false;
  matchedContact.value = null;
};

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({ accountId, id: conversationId })
    );
    router.push({ path });
  }
};

const placeCall = async contact => {
  try {
    const response = await store.dispatch('contacts/initiateCall', {
      contactId: contact.id,
      inboxId: selectedInboxId.value,
    });

    const { call_sid: callSid, conversation_id: conversationId } =
      response || {};
    callsStore.addCall({
      callSid,
      conversationId,
      inboxId: selectedInboxId.value,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
    });

    close();
    navigateToConversation(conversationId);
  } catch (error) {
    useAlert(error?.message || t('DIALPAD.ERRORS.CALL_FAILED'));
  }
};

const handleCall = async () => {
  if (isProcessing.value || !selectedInboxId.value) return;

  if (!isNumberValid.value) {
    useAlert(t('DIALPAD.ERRORS.INVALID_NUMBER'));
    return;
  }

  isProcessing.value = true;

  try {
    let contact = matchedContact.value;

    if (!contact && !showNameField.value) {
      contact = await findContactByPhone(normalizedNumber.value);
      if (!contact) {
        showNameField.value = true;
        return;
      }
      matchedContact.value = contact;
    }

    if (!contact) {
      const name = customerName.value.trim();
      if (!name) return;

      try {
        contact = await createContact({
          name,
          phoneNumber: normalizedNumber.value,
        });
      } catch (error) {
        useAlert(t('DIALPAD.ERRORS.CREATE_FAILED'));
        return;
      }
      matchedContact.value = contact;
    }

    await placeCall(contact);
  } catch (error) {
    useAlert(t('DIALPAD.ERRORS.CALL_FAILED'));
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('DIALPAD.TITLE')"
    width="sm"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="rawNumber"
        type="tel"
        :placeholder="t('DIALPAD.PLACEHOLDER')"
        autofocus
        @enter="handleCall"
      />

      <div class="grid grid-cols-3 gap-2">
        <button
          v-for="key in DIALPAD_KEYS"
          :key="key"
          type="button"
          class="flex items-center justify-center py-3 text-lg font-medium transition-colors rounded-lg text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2"
          @click="onDigitPress(key)"
        >
          {{ key }}
        </button>
      </div>

      <Button
        v-tooltip.top="t('DIALPAD.BACKSPACE')"
        variant="faded"
        color="slate"
        icon="i-lucide-delete"
        :aria-label="t('DIALPAD.BACKSPACE')"
        :disabled="!rawNumber"
        class="self-end"
        type="button"
        @click="onBackspace"
      />

      <div v-if="hasMultipleInboxes" class="flex flex-col gap-1">
        <Select
          v-model="selectedInboxId"
          :options="inboxOptions"
          class="w-full"
        />
      </div>

      <div v-if="showNameField" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-11">
          {{ t('DIALPAD.CUSTOMER_NAME_LABEL') }}
        </label>
        <Input
          v-model="customerName"
          :placeholder="t('DIALPAD.CUSTOMER_NAME_PLACEHOLDER')"
          @enter="handleCall"
        />
      </div>

      <p v-if="isCallingStatusVisible" class="mb-0 text-sm text-n-slate-11">
        {{ t('DIALPAD.CALLING', { name: callingName }) }}
      </p>

      <Button
        :label="callButtonLabel"
        :is-loading="isProcessing"
        :disabled="callButtonDisabled"
        color="blue"
        class="w-full"
        type="button"
        @click="handleCall"
      />
    </div>
  </Dialog>
</template>
